import axios from 'axios';

// ── Base URL — reads from env, falls back to production URL ───────
const BASE_URL =  'https://api.erisconnect.co.in';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor — attach JWT token ────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('ejt_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ── Response interceptor ──────────────────────────────────────────
api.interceptors.response.use(
  (response) => {
    const body = response.data;
    // Backend returns { isError, message, data, code }
    if (body && typeof body.isError !== 'undefined') {
      if (body.isError) {
        const err     = new Error(body.message || 'Something went wrong');
        err.code      = body.code;
        err.apiData   = body;
        return Promise.reject(err);
      }
      return { data: body.data, message: body.message, code: body.code };
    }
    return response;
  },
  (error) => {
    const res    = error.response;
    const status = res?.status;

    // ── 401 — session expired OR bad login credentials ───────────
    if (status === 401) {
      // If the 401 came from the login endpoint itself, it means wrong
      // username/password — show the error, do NOT redirect to /login
      const isLoginRequest = error.config?.url?.includes('/auth/login') ||
                             error.config?.url?.includes('/auth/');
      if (!isLoginRequest) {
        // Genuine session expiry — clear token and redirect
        localStorage.removeItem('ejt_token');
        localStorage.removeItem('ejt_user');
        window.location.href = '/login';
        return Promise.reject(new Error('Session expired. Please log in again.'));
      }
      // Fall through — let the backend message show as-is
    }

    // ── Structured backend error body ─────────────────────────────
    const body = res?.data;
    if (body && typeof body.isError !== 'undefined') {
      const err    = new Error(body.message || 'Request failed');
      err.code     = body.code;
      err.apiData  = body;
      return Promise.reject(err);
    }

    // ── No response — network / CORS / server down ────────────────
    if (!res) {
      // Determine the actual failure reason
      let msg = `Cannot reach the server at ${BASE_URL}.`;

      if (error.code === 'ECONNABORTED') {
        msg = 'Request timed out. The server took too long to respond. Please try again.';
      } else if (error.message?.includes('Network Error') || error.message?.includes('Failed to fetch')) {
        msg = [
          `Cannot connect to ${BASE_URL}.`,
          'Possible causes:',
          '• The API server is not running',
          '• CORS is not configured on the backend',
          '• SSL certificate issue',
          '• Firewall / network blocking the request',
        ].join('\n');
      }

      return Promise.reject(new Error(msg));
    }

    // ── 4xx / 5xx with no structured body ────────────────────────
    if (status === 403) return Promise.reject(new Error('Access denied. You do not have permission.'));
    if (status === 404) return Promise.reject(new Error('Resource not found.'));
    if (status === 500) return Promise.reject(new Error('Internal server error. Please contact support.'));
    if (status === 503) return Promise.reject(new Error('Server is temporarily unavailable. Please try again.'));

    return Promise.reject(new Error(res?.data?.message || `Request failed with status ${status}`));
  },
);

export default api;